import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FiSearch, FiFilter } from 'react-icons/fi';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../config/firebase';
import { formatCurrency } from '../utils/currency';
import '../styles/App.css';

function Raffles() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [raffles, setRaffles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRaffles = async () => {
      try {
        setLoading(true);
        const q = query(collection(db, 'raffles'), orderBy('createdAt', 'desc'));
        const snapshot = await getDocs(q);
        const rafflesData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setRaffles(rafflesData);
      } catch (error) {
        console.error('Error fetching raffles:', error);
        setRaffles([]);
      } finally {
        setLoading(false);
      }
    };

    fetchRaffles();
  }, []);

  const filteredRaffles = raffles.filter(raffle => {
    const matchesSearch = raffle.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          raffle.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'all' || raffle.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  // getCurrencySymbol removed — use formatCurrency() instead

  if (loading) {
    return (
      <div className="raffles-section fade-in">
        <h2>{t('nav.raffles')}</h2>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <div className="spinner" style={{
            border: '4px solid var(--primary-light)',
            borderTop: '4px solid var(--primary)',
            borderRadius: '50%',
            width: '40px',
            height: '40px',
            animation: 'spin 1s linear infinite'
          }} />
        </div>
      </div>
    );
  }

  return (
    <div className="raffles-section fade-in">
      <h2>{t('nav.raffles')}</h2>
      <p style={{ color: 'var(--dark-light)', marginBottom: '1.5rem' }}>
        {t('hero.subtitle')}
      </p>

      {raffles.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">🎰</div>
          <h3>{t('myRaffles.noRafflesYet')}</h3>
          <button
            className="btn btn-primary"
            onClick={() => navigate('/create')}
            style={{ marginTop: '1rem' }}
          >
            {t('raffle.create')}
          </button>
        </div>
      ) : (
        <>
          {/* Search and Filter */}
          <div style={{ display: 'flex', gap: '1rem', marginBottom: '2rem', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: '250px', position: 'relative' }}>
              <FiSearch style={{ position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--dark-light)' }} />
              <input
                type="text"
                className="form-control"
                placeholder={t('common.search') + '...'}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ paddingLeft: '2.5rem' }}
              />
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <FiFilter style={{ color: 'var(--dark-light)' }} />
              {['all', 'active', 'completed'].map(status => (
                <button
                  key={status}
                  className={`btn btn-sm ${filterStatus === status ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => setFilterStatus(status)}
                  style={{ textTransform: 'capitalize' }}
                >
                  {status === 'all' ? t('common.filter') + ': All' : t(`raffle.${status}`)}
                </button>
              ))}
            </div>
          </div>

          {/* Raffles Grid */}
          <div className="raffles-grid">
            {filteredRaffles.map(raffle => (
              <div
                key={raffle.id}
                className="raffle-card"
                onClick={() => navigate(raffle.slug ? `/rifa/${raffle.slug}` : `/raffle/${raffle.id}`)}
              >
                <div className="raffle-card-image">
                  {raffle.imageUrl ? (
                    <img src={raffle.imageUrl} alt={raffle.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <span>{raffle.emoji || '🎰'}</span>
                  )}
                  <span className={`raffle-card-badge badge-${raffle.status}`}>
                    {t(`raffle.${raffle.status}`)}
                  </span>
                </div>
                <div className="raffle-card-body">
                  <h3>{raffle.title}</h3>
                  <p>{raffle.description ? raffle.description.replace(/[#*_~`>|\-\[\]()!]/g, '').substring(0, 150) : ''}</p>
                  <div className="raffle-card-meta">
                    <span className="raffle-price">
                      {formatCurrency(raffle.ticketPrice, raffle.currency || 'BRL')}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {filteredRaffles.length === 0 && raffles.length > 0 && (
            <div className="empty-state">
              <div className="empty-icon">🔍</div>
              <h3>Nenhuma rifa encontrada</h3>
              <p>Tente ajustar sua busca ou filtro</p>
            </div>
          )}
        </>
      )}

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

export default Raffles;
